import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Share2, Users, CalendarClock, DoorOpen, Clock, MessageCircle, Quote } from "lucide-react";
import { api } from "../lib/api";
import { usePersonalization } from "../lib/personalization";
import { useBranding } from "../components/Layout";
import { normalizeActivityStats, formatStatNumber } from "../lib/activityStats";

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (d.getTime() === today.getTime()) return "Today";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtClock = (t) => {
  if (!t) return "";
  const s = String(t).trim();
  // Not a strict HH:MM means the agent typed it that way ("4:30 PM",
  // "11am - 1pm"), so hand it back rather than bolting a second AM/PM on.
  if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s;
  const [hh, mm] = s.split(":");
  const hr = parseInt(hh, 10);
  if (Number.isNaN(hr)) return t;
  const ampm = hr >= 12 ? "PM" : "AM";
  return `${((hr + 11) % 12) + 1}:${mm} ${ampm}`;
};

const STAT_TILES = [
  { key: "social_views", label: "Social views", icon: <Share2 size={16} /> },
  { key: "listing_views", label: "Listing views", icon: <Eye size={16} /> },
  { key: "engagements", label: "Engagements", icon: <Users size={16} /> },
  { key: "showings_count", label: "Showings", icon: <CalendarClock size={16} /> },
  { key: "open_house_groups", label: "Open house groups", icon: <DoorOpen size={16} /> },
];

function daysOnMarket(listingLaunchIso) {
  if (!listingLaunchIso) return null;
  const start = new Date(`${listingLaunchIso}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - start) / 86400000);
  return diff >= 0 ? diff : null;
}

export default function Activity() {
  const { p } = usePersonalization();
  const branding = useBranding();

  const [feedback, setFeedback] = useState([]);
  const [fbCounts, setFbCounts] = useState({ total: 0, showing: 0, open_house: 0 });
  const [showings, setShowings] = useState([]);
  // An empty list means "not back yet", not "none" - without this the two
  // showing tiles would flash zero on every load before the calendar lands.
  const [showingsLoaded, setShowingsLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/me/feedback").then(({ data }) => {
      if (!alive) return;
      setFeedback(data.feedback || []);
      setFbCounts(data.counts || { total: 0, showing: 0, open_house: 0 });
    }).catch(() => {});
    api.get("/me/showings").then(({ data }) => {
      if (alive) setShowings(data.showings || []);
    }).catch(() => {}).finally(() => {
      if (alive) setShowingsLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const visits = useMemo(() => {
    const live = showings.filter((s) => s.status !== "cancelled");
    const split = (kind) => {
      const of = live.filter((s) => (s.kind || "showing") === kind);
      return {
        upcoming: of.filter((s) => s.date >= today).sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)),
        past: of.filter((s) => s.date < today).sort((a, b) => `${b.date}${b.start_time}`.localeCompare(`${a.date}${a.start_time}`)),
      };
    };
    const oh = split("open_house");
    return {
      showings: split("showing"),
      openHouses: oh,
      totalGroups: oh.past.concat(oh.upcoming).reduce((n, s) => n + (s.groups || 0), 0),
    };
  }, [showings, today]);

  // Defined here because the visits card below is gated on it; without this
  // the whole page throws the moment a seller has any activity to show.
  const hasVisits =
    visits.showings.past.length + visits.showings.upcoming.length +
    visits.openHouses.past.length + visits.openHouses.upcoming.length > 0;

  // The two showing tiles are counted from the very list rendered further down
  // this page, so the headline number and the showings under it are always the
  // same data. Every other tile is the number the agent saved.
  const stats = useMemo(() => {
    const base = normalizeActivityStats(p.activity_stats);
    if (!showingsLoaded) return base;
    let privateShowings = 0;
    let groups = 0;
    showings.forEach((s) => {
      if (s.status === "cancelled") return;
      if ((s.kind || "showing") === "open_house") {
        groups += Math.max(0, parseInt(s.groups, 10) || 0);
      } else {
        privateShowings += 1;
      }
    });
    return {
      ...base,
      showings_count: { ...base.showings_count, value: privateShowings },
      open_house_groups: { ...base.open_house_groups, value: groups },
    };
  }, [p.activity_stats, showings, showingsLoaded]);

  const dom = useMemo(() => daysOnMarket(p.key_dates?.listing_launch), [p.key_dates]);

  // Only stats/sources the agent has switched on ever reach the seller.
  const visibleTiles = useMemo(
    () => STAT_TILES.filter((t) => stats[t.key]?.enabled !== false),
    [stats]
  );
  // An unnamed source is a half-finished row in the admin — don't show it.
  const visiblePlatforms = useMemo(
    () => stats.platforms.filter((r) => r.enabled !== false && r.label.trim()),
    [stats]
  );

  const isEmpty = visibleTiles.every((t) => !stats[t.key]?.value) && visiblePlatforms.length === 0;

  const tileCount = visibleTiles.length + (dom !== null ? 1 : 0);

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">Home activity</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        How your home is doing out there.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 720, fontSize: "1.05rem" }}>
        {branding.agent_first_name} updates these numbers as your listing gets seen, saved, and shown.
      </p>

      {isEmpty ? (
        <div className="card" style={{ padding: 32, marginTop: 28, color: "var(--text-muted)" }} data-testid="activity-empty">
          <p style={{ margin: 0 }}>
            No activity yet — once your home is live, {branding.agent_first_name} will start tracking views, showings,
            and interest here.
          </p>
          <Link to="/ask" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)", marginTop: 14, fontSize: 14 }}>
            <MessageCircle size={14} /> Ask {branding.agent_first_name} a question
          </Link>
        </div>
      ) : (
        <>
          {visibleTiles.length > 0 && (
            <div
              className="card"
              style={{
                marginTop: 28,
                padding: 0,
                display: "grid",
                gridTemplateColumns: `repeat(${tileCount}, 1fr)`,
                overflowX: "auto",
              }}
              data-testid="activity-stat-strip"
            >
              {visibleTiles.map((t, i) => (
                <div
                  key={t.key}
                  style={{
                    padding: "22px 16px",
                    textAlign: "center",
                    borderLeft: i === 0 ? "none" : "1px solid var(--border)",
                    minWidth: 110,
                  }}
                  data-testid={`activity-tile-${t.key}`}
                >
                  <div style={{ display: "flex", justifyContent: "center", color: "var(--primary)", marginBottom: 6 }}>{t.icon}</div>
                  <div className="font-serif" style={{ fontSize: 26, fontWeight: 500 }}>{formatStatNumber(stats[t.key]?.value)}</div>
                  <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 4 }}>{t.label}</div>
                </div>
              ))}
              {dom !== null && (
                <div style={{ padding: "22px 16px", textAlign: "center", borderLeft: visibleTiles.length > 0 ? "1px solid var(--border)" : "none", minWidth: 110 }} data-testid="activity-tile-dom">
                  <div style={{ display: "flex", justifyContent: "center", color: "var(--primary)", marginBottom: 6 }}><Clock size={16} /></div>
                  <div className="font-serif" style={{ fontSize: 26, fontWeight: 500 }}>{dom}</div>
                  <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 4 }}>Days on market</div>
                </div>
              )}
            </div>
          )}

          {visiblePlatforms.length > 0 && (
            <div className="card" style={{ overflow: "hidden", marginTop: 28 }} data-testid="activity-platforms">
              <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                <span className="font-serif" style={{ fontSize: 22, fontWeight: 500 }}>Where it's being seen</span>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>Views and interest across every place your listing lives.</div>
              </div>
              <div>
                {visiblePlatforms.map((r) => (
                  <div
                    key={r.id}
                    style={{ padding: "16px 24px", borderTop: "1px solid var(--border)", display: "grid", gridTemplateColumns: "1fr auto auto", gap: 24, alignItems: "center" }}
                    data-testid={`activity-platform-${r.id}`}
                  >
                    <div style={{ fontWeight: 500 }}>{r.label}</div>
                    <div style={{ textAlign: "right", minWidth: 90 }}>
                      <div style={{ fontWeight: 500 }}>{formatStatNumber(r.views)}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>Views</div>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 90 }}>
                      <div style={{ fontWeight: 500 }}>{formatStatNumber(r.metric_value)}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{r.metric_label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasVisits && (
            <div className="card" style={{ overflow: "hidden", marginTop: 28 }} data-testid="activity-visits">
              <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                <span className="font-serif" style={{ fontSize: 22, fontWeight: 500 }}>Showings &amp; open houses</span>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>Who's been through your home, and what's booked.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                <VisitColumn
                  title="Private showings"
                  summary={`${visits.showings.past.length} held${visits.showings.upcoming.length ? ` · ${visits.showings.upcoming.length} booked` : ""}`}
                  upcoming={visits.showings.upcoming}
                  past={visits.showings.past}
                  testid="visits-showings"
                />
                <VisitColumn
                  title="Open houses"
                  summary={
                    visits.totalGroups
                      ? `${visits.totalGroups} group${visits.totalGroups === 1 ? "" : "s"} through`
                      : `${visits.openHouses.past.length} held${visits.openHouses.upcoming.length ? ` · ${visits.openHouses.upcoming.length} booked` : ""}`
                  }
                  upcoming={visits.openHouses.upcoming}
                  past={visits.openHouses.past}
                  showGroups
                  bordered
                  testid="visits-openhouses"
                />
              </div>
            </div>
          )}

          {feedback.length > 0 && (
            <div className="card" style={{ overflow: "hidden", marginTop: 28 }} data-testid="activity-feedback">
              <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                <span className="font-serif" style={{ fontSize: 22, fontWeight: 500 }}>What buyers are saying</span>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                  {fbCounts.total} note{fbCounts.total === 1 ? "" : "s"}
                  {fbCounts.showing ? ` · ${fbCounts.showing} from showings` : ""}
                  {fbCounts.open_house ? ` · ${fbCounts.open_house} from open houses` : ""}
                </div>
              </div>
              <div>
                {feedback.map((f) => (
                  <div key={f.feedback_id} data-testid={`feedback-${f.feedback_id}`}
                       style={{ padding: "18px 24px", borderTop: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--primary)", fontWeight: 600 }}>
                        {f.kind === "open_house" ? "Open house" : "Private showing"}
                      </span>
                      <span style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
                        {fmtDay(f.date)}{f.kind === "open_house" && f.groups ? ` · ${f.groups} groups` : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                      <Quote size={15} style={{ color: "var(--border)", flexShrink: 0, marginTop: 3 }} />
                      <div style={{ lineHeight: 1.65 }}>{f.body}</div>
                    </div>
                    {f.source && (
                      <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 6, marginLeft: 25 }}>{f.source}</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55 }}>
                Private to you. Buyer names are never shown &mdash; feedback is attributed to the buyer's brokerage only.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function VisitColumn({ title, summary, upcoming, past, showGroups, bordered, testid }) {
  return (
    <div style={{ padding: "18px 24px", borderLeft: bordered ? "1px solid var(--border)" : undefined }} data-testid={testid}>
      <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{title}</div>
      <div className="font-serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 4 }}>{summary}</div>

      {upcoming.length > 0 && (
        <>
          <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--primary)", fontWeight: 600, marginTop: 16 }}>Coming up</div>
          {upcoming.map((s) => (
            <div key={s.id} style={{ marginTop: 8, fontSize: 14 }}>
              {fmtDay(s.date)} · {fmtClock(s.start_time)}{s.end_time ? ` – ${fmtClock(s.end_time)}` : ""}
            </div>
          ))}
        </>
      )}

      {past.length > 0 && (
        <>
          <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600, marginTop: 16 }}>Already held</div>
          {past.slice(0, 6).map((s) => (
            <div key={s.id} style={{ marginTop: 8, fontSize: 14, display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span>{fmtDay(s.date)}</span>
              {showGroups && s.groups ? (
                <span style={{ color: "var(--text-muted)" }}>{s.groups} group{s.groups === 1 ? "" : "s"}</span>
              ) : null}
            </div>
          ))}
          {past.length > 6 && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--text-muted)" }}>+ {past.length - 6} more</div>
          )}
        </>
      )}

      {upcoming.length === 0 && past.length === 0 && (
        <div style={{ marginTop: 12, color: "var(--text-muted)", fontSize: 13.5 }}>Nothing scheduled yet.</div>
      )}
    </div>
  );
}
